import { useCallback, useEffect, useRef } from 'react';

export default function useWakeLock(isEnabled = true) {
  const wakeLockRef = useRef(null);

  // useCallback so the effect below can honestly declare it as a dependency:
  // a fresh identity every render would have re-registered the listeners (and
  // re-requested the lock) on every parent render.
  const requestWakeLock = useCallback(async () => {
    if (!isEnabled) return;
    if (!('wakeLock' in navigator)) return;
    try {
      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;
      lock.addEventListener('release', () => {
        wakeLockRef.current = null;
      });
    } catch {
      // Swallow errors silently; API can reject if not user-initiated or unsupported
    }
  }, [isEnabled]);

  useEffect(() => {
    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    const handleWindowFocus = () => {
      requestWakeLock();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      if (wakeLockRef.current) {
        try {
          wakeLockRef.current.release();
        } catch {
          /* already released, or the tab lost it — nothing to do */
        }
        wakeLockRef.current = null;
      }
    };
  }, [requestWakeLock]);
}
